import { useState } from 'react';
import { getInitials, getAvatarColor } from './avatarUtils';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const SIZE_MAP: Record<AvatarSize, { wrap: string; text: string }> = {
  xs:  { wrap: 'w-7 h-7',   text: 'text-xs' },
  sm:  { wrap: 'w-9 h-9',   text: 'text-xs' },
  md:  { wrap: 'w-11 h-11', text: 'text-sm' },
  lg:  { wrap: 'w-16 h-16', text: 'text-lg' },
  xl:  { wrap: 'w-20 h-20', text: 'text-xl' },
  '2xl': { wrap: 'w-28 h-28', text: 'text-3xl' },
};

interface AvatarDisplayProps {
  name?: string | null;
  avatarUrl?: string | null;
  size?: AvatarSize;
  className?: string;
  /** Ring style - e.g. "ring-2 ring-white" */
  ring?: string;
}

/**
 * Pure display component. Shows avatar photo if available, otherwise
 * renders a colour‑coded initials placeholder.
 */
export default function AvatarDisplay({
  name,
  avatarUrl,
  size = 'md',
  className = '',
  ring = '',
}: AvatarDisplayProps) {
  const [imgError, setImgError] = useState(false);
  const { wrap, text } = SIZE_MAP[size];
  const colorCls = getAvatarColor(name);
  const initials = getInitials(name);

  const base = `${wrap} rounded-full shrink-0 overflow-hidden ${ring} ${className}`;

  if (avatarUrl && !imgError) {
    return (
      <div className={base}>
        <img
          src={avatarUrl}
          alt={name ?? '头像'}
          className="w-full h-full object-cover object-top"
          onError={() => setImgError(true)}
        />
      </div>
    );
  }

  return (
    <div
      className={`${base} flex items-center justify-center font-semibold ${text} ${colorCls}`}
    >
      {initials}
    </div>
  );
}
